import {
  createContext,
  ReactNode,
  useRef,
  useMemo,
  useContext,
  useCallback,
} from 'react';
import debounce from 'lodash/debounce';
import { JSONContent } from '@tiptap/core';

import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import { modifyActivitiesPublishChanges } from 'approot/activity-builder/activity-builder.apollo.utils';
import { SET_SUGGESTED_ANSWERS } from '../suggested-answers/suggested-answers.graphql';
import type { SuggestedAnswerFragment } from '../suggested-answers/suggested-answers.graphql.generated';
import { InquisitiveActivitiesSuggestedAnswerType } from 'global/graphql/scalars.type';
import { updateSuggestedAnswerInCache } from '../suggested-answers/suggested-answer.apollo';
import { useActivityBuilderRequest } from 'approot/activity-builder/activity-builder-request.apollo';
import { getSuggestedAnswerItemPayload } from './logic/get-suggested-answer-item-payload';
import type {
  SetSuggestedAnswersMutation,
  SetSuggestedAnswersMutationVariables,
} from '../suggested-answers/suggested-answers.graphql.generated';
import type { Pluck } from 'lib/gql/pluck';

type SuggestedAnswer = Omit<
  Pluck<SuggestedAnswerFragment, 'suggestedAnswers'>,
  'contentId' | 'contentType'
>;

export type AnswerInput = {
  __typename: 'ComponentSuggestedAnswersProperties';
  suggestedAnswersScore: number;
  suggestedAnswerType: InquisitiveActivitiesSuggestedAnswerType;
  suggestedAnswers: SuggestedAnswer[] | null;
  markingGuides:
    | {
        id: string;
        mark: number | null;
        description: JSONContent | null;
        __typename: 'MarkingGuide';
      }[]
    | null;
};

type BatchComponentUpdate = {
  loading: boolean;
  updateSuggestedAnswerApollo: (props: {
    componentId: string;
    input: AnswerInput;
    typename: string;
  }) => void;
  updateSuggestedAnswer: (props: {
    componentId: string;
    input: AnswerInput;
    typename: string;
  }) => void;
};

export const BatchSuggestedAnswersContext = createContext<BatchComponentUpdate>(
  {
    loading: false,
    updateSuggestedAnswer: () => {},
    updateSuggestedAnswerApollo: () => {},
  }
);

export const BatchSuggestedAnswersContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const syncMapRef = useRef<Map<string, Function>>(new Map());

  const { showError } = useSnackbar();
  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const { contentId, contentType } = useGetInteractiveLessonParams();

  const [upsert, { loading, client }] = useLoggingMutation<
    SetSuggestedAnswersMutation,
    SetSuggestedAnswersMutationVariables
  >(SET_SUGGESTED_ANSWERS);

  const syncToServer = useCallback(
    async ({
      componentId,
      input,
      requestId,
      onComplete,
    }: {
      componentId: string;
      input: AnswerInput;
      requestId: string;
      onComplete: (requestId: string) => void;
    }) => {
      await upsert({
        fetchPolicy: 'no-cache',
        ignoreResults: true,
        variables: {
          input: {
            contentId,
            contentType,
            componentId,
            ...getSuggestedAnswerPayload(input),
          },
        },
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {
          showError(
            'There was an error updating the suggested answer. Please refresh and try again'
          );
        })
        .finally(() => {
          onComplete?.(requestId);
        });
    },
    [client, contentId, contentType, upsert, showError]
  );

  const updateSuggestedAnswerApollo = useCallback(
    ({
      componentId,
      input,
      typename,
    }: {
      componentId: string;
      input: AnswerInput;
      typename: string;
    }) => {
      updateSuggestedAnswerInCache({
        client,
        id: componentId,
        typename,
        properties: {
          ...input,
          suggestedAnswers:
            input.suggestedAnswers?.map(s => ({
              ...s,
              contentId,
              contentType,
            })) || null,
        },
      });
    },
    [client, contentId, contentType]
  );

  const updateSuggestedAnswer = useCallback(
    ({
      componentId,
      input,
      typename,
    }: {
      componentId: string;
      input: AnswerInput;
      typename: string;
    }) => {
      if (!componentId) return;

      updateSuggestedAnswerApollo({
        componentId,
        input,
        typename,
      });

      if (!syncMapRef.current.get(componentId)) {
        // create a new debounce function for this componentId if it doesn't exist
        syncMapRef.current.set(
          componentId,
          debounce((componentId, input, onComplete) => {
            const requestId = createRequest(componentId);
            syncToServer({ componentId, input, requestId, onComplete });
          }, 300)
        );
      }

      // call the debounced function for this componentId
      const sync = syncMapRef.current.get(componentId);
      if (sync) {
        sync(componentId, input, (requestId: string) => {
          clearRequest(requestId);
        });
      }
    },
    [updateSuggestedAnswerApollo, syncToServer, createRequest, clearRequest]
  );

  const value = useMemo(
    () => ({
      loading,
      updateSuggestedAnswer,
      updateSuggestedAnswerApollo,
    }),
    [loading, updateSuggestedAnswer, updateSuggestedAnswerApollo]
  );

  return (
    <BatchSuggestedAnswersContext.Provider value={value}>
      {children}
    </BatchSuggestedAnswersContext.Provider>
  );
};

export const useBatchSuggestedAnswersContext = () => {
  const { loading, updateSuggestedAnswer, updateSuggestedAnswerApollo } =
    useContext(BatchSuggestedAnswersContext);

  const handleUpdate = useCallback(
    (props: { componentId: string; input: AnswerInput; typename: string }) => {
      updateSuggestedAnswer(props);
    },
    [updateSuggestedAnswer]
  );

  return {
    loading,
    handleUpdate,
    updateSuggestedAnswer,
    updateSuggestedAnswerApollo,
  };
};

export function getSuggestedAnswerPayload(input: AnswerInput) {
  return {
    suggestedAnswerType: input.suggestedAnswerType,
    suggestedAnswersScore: input.suggestedAnswersScore,
    suggestedAnswers:
      input.suggestedAnswers?.map(s => getSuggestedAnswerItemPayload(s)) || [],
  };
}
